import type { SearchHit } from "@/services-frontend/search/searchClient";

export type BucketKey = "today" | "yesterday" | "past-week" | "past-30-days" | "older";

export interface Bucket {
  key: BucketKey;
  label: string;
  hits: SearchHit[];
}

/**
 * Bucket order for rendering. Keys not represented in the result set are
 * collapsed (empty buckets are not rendered by the modal).
 */
export const BUCKET_ORDER: { key: BucketKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "past-week", label: "Past week" },
  { key: "past-30-days", label: "Past 30 days" },
  { key: "older", label: "Older" },
];

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Splits a flat (updatedAt-sorted) hit list into time
 * buckets. Assumes input is already sorted desc by `updatedAt`; we just
 * walk it linearly. Keeps each bucket in the same desc order.
 */
export function bucketByDate(hits: SearchHit[], now = Date.now()): Bucket[] {
  const todayStart = startOfDay(now);
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const weekStart = todayStart - 6 * 24 * 60 * 60 * 1000; // last 7 days including today
  const thirtyStart = todayStart - 29 * 24 * 60 * 60 * 1000;

  const byKey: Record<BucketKey, SearchHit[]> = {
    today: [],
    yesterday: [],
    "past-week": [],
    "past-30-days": [],
    older: [],
  };

  for (const hit of hits) {
    const t = hit.updatedAt || hit.createdAt || 0;
    if (!t) {
      byKey["older"].push(hit);
      continue;
    }
    if (t >= todayStart) byKey["today"].push(hit);
    else if (t >= yesterdayStart) byKey["yesterday"].push(hit);
    else if (t >= weekStart) byKey["past-week"].push(hit);
    else if (t >= thirtyStart) byKey["past-30-days"].push(hit);
    else byKey["older"].push(hit);
  }

  return BUCKET_ORDER.map(({ key, label }) => ({
    key,
    label,
    hits: byKey[key],
  })).filter((b) => b.hits.length > 0);
}
